import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, Plus, Loader2 } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { ServiceCard } from '../components/common/ServiceCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ServicesPage() {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'recent');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchServices();
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      if (searchParams.get('category')) params.append('category', searchParams.get('category'));
      if (searchParams.get('sort')) params.append('sort', searchParams.get('sort'));
      params.append('limit', '30');

      const response = await axios.get(`${API_URL}/services?${params.toString()}`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters({ search });
  };

  const updateFilters = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  return (
    <Layout>
      <div className="container-app py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Services</h1>
            <p className="text-muted-foreground">
              Découvrez les services proposés par la communauté
            </p>
          </div>
          {isAuthenticated && (
            <Link to="/services/new">
              <Button className="btn-primary" data-testid="create-service-btn">
                <Plus className="w-4 h-4 mr-2" />
                Proposer un service
              </Button>
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="bg-card rounded-2xl border border-border p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un service..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="search-input"
                />
              </div>
            </form>

            {/* Category Filter */}
            <Select
              value={category}
              onValueChange={(value) => {
                setCategory(value);
                updateFilters({ category: value === 'all' ? '' : value });
              }}
            >
              <SelectTrigger className="w-full md:w-[200px] rounded-xl" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.slug}>
                    {cat.icon} {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select
              value={sort}
              onValueChange={(value) => {
                setSort(value);
                updateFilters({ sort: value });
              }}
            >
              <SelectTrigger className="w-full md:w-[180px] rounded-xl" data-testid="sort-filter">
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Plus récents</SelectItem>
                <SelectItem value="popular">Plus populaires</SelectItem>
                <SelectItem value="rating">Mieux notés</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Filters */}
          {(searchParams.get('search') || searchParams.get('category')) && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
              <span className="text-sm text-muted-foreground">Filtres actifs :</span>
              {searchParams.get('search') && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="rounded-full h-7 text-xs"
                  onClick={() => {
                    setSearch('');
                    updateFilters({ search: '' });
                  }}
                >
                  "{searchParams.get('search')}" ×
                </Button>
              )}
              {searchParams.get('category') && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="rounded-full h-7 text-xs"
                  onClick={() => {
                    setCategory('');
                    updateFilters({ category: '' });
                  }}
                >
                  {categories.find(c => c.slug === searchParams.get('category'))?.name} ×
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : services.length > 0 ? (
          <>
            <p className="text-muted-foreground mb-6">
              {services.length} service{services.length > 1 ? 's' : ''} trouvé{services.length > 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
              {services.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold mb-2">Aucun service trouvé</h3>
            <p className="text-muted-foreground mb-6">
              Essayez de modifier vos critères de recherche
            </p>
            {isAuthenticated && (
              <Link to="/services/new">
                <Button className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Proposer un service
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
